import React, { useRef, Children } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
interface AnimatedSectionProps {
  children: ReactNode;
  className?: string;
  delay?: number;
}
export function AnimatedSection({
  children,
  className = '',
  delay = 0
}: AnimatedSectionProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} className={className} data-id="element-109">
      {children}
    </motion.div>;
}
export function ParallaxLayer({
  children,
  className = '',
  speed = 0.2,
  direction = 'y',
  scale = 0
}: {
  children: ReactNode;
  className?: string;
  speed?: number;
  direction?: 'y' | 'x';
  scale?: number;
}) {
  const ref = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const translateRange = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [-300 * speed, 300 * speed]);
  // Scale: element starts small, peaks at center of viewport, then shrinks
  // e.g. scale=0.3 means it goes from 0.7 → 1.0 → 0.7
  const scaleRange = useTransform(scrollYProgress, [0, 0.5, 1], isMobile || scale === 0 ? [1, 1, 1] : [1 - scale, 1 + scale * 0.5, 1 - scale]);
  const smoothTranslate = useSpring(translateRange, {
    stiffness: 100,
    damping: 30
  });
  const smoothScale = useSpring(scaleRange, {
    stiffness: 80,
    damping: 20
  });
  return <motion.div ref={ref} style={{
    ...(direction === 'x' ? {
      x: smoothTranslate
    } : {
      y: smoothTranslate
    }),
    scale: scale !== 0 ? smoothScale : undefined
  }} className={`will-change-transform ${className}`} data-id="element-110">
      {children}
    </motion.div>;
}
export function StaggerContainer({
  children,
  className = ''
}: {
  children: ReactNode;
  className?: string;
}) {
  return <motion.div initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-100px'
  }} variants={{
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  }} className={className} data-id="element-111">
      {children}
    </motion.div>;
}
export function StaggerItem({
  children,
  className = ''
}: {
  children: ReactNode;
  className?: string;
}) {
  return <motion.div variants={{
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  }} className={className} data-id="element-112">
      {children}
    </motion.div>;
}